import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { trackFormSubmission, trackButtonClick, trackPricingInteraction } from '../utils/analytics';
import { EMAILJS_CONFIG, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL, initializeEmailJS } from '../utils/emailConfig';
import { PACKAGE_PRICES, PASSENGER_PRICE, PHOTOGRAPHER_PRICE, GOPRO_PRICE, MERCH_PRICE, MAX_MOTORCYCLES, formatPrice, packageName, calculateQuote } from '../utils/pricing';
import './Booking.css';

const Booking = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    package: '3-day',
    motorcycles: 1,
    passengers: 0,
    photographer: false,
    goproQty: 0,
    merchQty: 0,
    experience: '',
    motorcycle: '',
    emergencyContact: '',
    emergencyPhone: '',
    specialRequests: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Initialize EmailJS when component mounts
  React.useEffect(() => {
    initializeEmailJS();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'motorcycles' || name === 'passengers' || name === 'goproQty' || name === 'merchQty') {
        next[name] = parseInt(value, 10) || 0;
      }

      if (name === 'motorcycles') {
        const bikes = Math.min(MAX_MOTORCYCLES, Math.max(1, next.motorcycles || 1));
        next.motorcycles = bikes;
        if (next.passengers > bikes) next.passengers = bikes;
        if (next.goproQty > bikes) next.goproQty = bikes;
        if (next.merchQty > bikes + next.passengers) next.merchQty = bikes + next.passengers;
      }

      if (name === 'passengers') {
        const bikes = prev.motorcycles || 1;
        next.passengers = Math.min(bikes, Math.max(0, next.passengers));
        if (next.merchQty > bikes + next.passengers) next.merchQty = bikes + next.passengers;
      }

      return next;
    });
    
    if (name === 'package') {
      trackPricingInteraction('package_selected', value, PACKAGE_PRICES[value]);
    }
  };

  const quote = calculateQuote({
    packageId: formData.package,
    motorcycles: formData.motorcycles,
    passengers: formData.passengers,
    photographer: formData.photographer,
    goproQty: formData.goproQty,
    merchQty: formData.merchQty
  });

  const quoteAddonsLabel = () => {
    const extras = [];
    if (formData.photographer) extras.push('Photographer');
    if (quote.gopros > 0) extras.push(`${quote.gopros} GoPro`);
    if (quote.merch > 0) extras.push(`${quote.merch} merch`);
    return extras.length > 0 ? extras.join(', ') : 'None';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    // Track form submission
    trackFormSubmission('booking_form', formData);
    
    try {
      // Prepare email template parameters
      const templateParams = {
        to_email: EMAILJS_CONFIG.TO_EMAIL,
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone,
        package: packageName(formData.package),
        motorcycles: quote.bikes,
        passengers: quote.pax,
        addons: quoteAddonsLabel(),
        experience: formData.experience,
        motorcycle: formData.motorcycle,
        emergency_contact: formData.emergencyContact,
        emergency_phone: formData.emergencyPhone,
        special_requests: formData.specialRequests || 'None',
        total_price: quote.total,
        message: `
New Devil's Highway Tour Booking!

Customer Information:
- Name: ${formData.firstName} ${formData.lastName}
- Email: ${formData.email}
- Phone: ${formData.phone}

Tour Details:
- Package: ${packageName(formData.package)}
- Motorcycles: ${quote.bikes}
- Passengers: ${quote.pax}
- Add-ons: ${quoteAddonsLabel()}

Price breakdown:
${quote.lines.map((line) => `- ${line.label}: $${formatPrice(line.amount)}`).join('\n')}
- Total: $${formatPrice(quote.total)}

Riding Information:
- Experience Level: ${formData.experience}
- Motorcycle(s): ${formData.motorcycle}

Emergency Contact:
- Name: ${formData.emergencyContact}
- Phone: ${formData.emergencyPhone}

Additional Information:
- Special Requests: ${formData.specialRequests || 'None'}

This booking was submitted from the Devil's Highway Motorcycle Tours website.
        `
      };

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      // Success - reset form and show success message
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        package: '3-day',
        motorcycles: 1,
        passengers: 0,
        photographer: false,
        goproQty: 0,
        merchQty: 0,
        experience: '',
        motorcycle: '',
        emergencyContact: '',
        emergencyPhone: '',
        specialRequests: ''
      });
      
    } catch (error) {
      console.error('Email sending failed:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const passengerOptions = Array.from({ length: quote.bikes + 1 }, (_, i) => i);
  const motorcycleOptions = Array.from({ length: MAX_MOTORCYCLES }, (_, i) => i + 1);

  return (
    <section id="booking" className="booking section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Book Your Adventure</h2>
          <p className="section-subtitle">
            Request a spot on the Devil's Highway. This is an inquiry, not a paid checkout — we'll follow up to confirm dates and payment.
          </p>
          <p className="booking-email-fallback">
            Just have a question? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </div>

        <div className="booking-content">
          <div className="booking-form-container">
            <form className="booking-form" onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Tour Selection</h3>
                <div className="form-group">
                  <label>Package *</label>
                  <div className="package-options">
                    <label className="package-option">
                      <input
                        type="radio"
                        name="package"
                        value="2-day"
                        checked={formData.package === '2-day'}
                        onChange={handleInputChange}
                      />
                      <div className="option-content">
                        <span className="option-name">2-Day Adventure</span>
                        <span className="option-price">${formatPrice(PACKAGE_PRICES['2-day'])}<span className="option-unit"> / motorcycle</span></span>
                      </div>
                    </label>
                    <label className="package-option">
                      <input
                        type="radio"
                        name="package"
                        value="3-day"
                        checked={formData.package === '3-day'}
                        onChange={handleInputChange}
                      />
                      <div className="option-content">
                        <span className="option-name">3-Day Ultimate</span>
                        <span className="option-price">${formatPrice(PACKAGE_PRICES['3-day'])}<span className="option-unit"> / motorcycle</span></span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="motorcycles">Number of Motorcycles *</label>
                    <select
                      id="motorcycles"
                      name="motorcycles"
                      value={formData.motorcycles}
                      onChange={handleInputChange}
                    >
                      {motorcycleOptions.map((count) => (
                        <option key={count} value={count}>
                          {count} {count === 1 ? 'motorcycle' : 'motorcycles'}
                        </option>
                      ))}
                    </select>
                    <p className="field-hint">Groups of 2–12 riders. Each motorcycle is ${formatPrice(quote.unit)}.</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="passengers">Number of Passengers</label>
                    <select
                      id="passengers"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleInputChange}
                    >
                      {passengerOptions.map((count) => (
                        <option key={count} value={count}>
                          {count === 0
                            ? 'No passengers'
                            : `${count} passenger${count === 1 ? '' : 's'} (+$${formatPrice(count * PASSENGER_PRICE)})`}
                        </option>
                      ))}
                    </select>
                    <p className="field-hint">Two-up only. One passenger per motorcycle, sharing the rider's room. ${formatPrice(PASSENGER_PRICE)} each.</p>
                  </div>
                </div>

                <div className="form-group">
                  <label>Optional extras <span className="optional-label">(not required)</span></label>
                  <p className="addon-hint">Leave these at zero unless you want them. They are never part of the base price.</p>
                  <div className="addon-options">
                    <label className="addon-option">
                      <input
                        type="checkbox"
                        name="photographer"
                        checked={formData.photographer}
                        onChange={handleInputChange}
                      />
                      <div className="addon-content">
                        <span className="addon-name">
                          Photographer
                          <span className="optional-badge">Optional</span>
                        </span>
                        <span className="addon-price">+${PHOTOGRAPHER_PRICE} for the group</span>
                      </div>
                    </label>
                    <div className="addon-option addon-qty">
                      <div className="addon-content">
                        <span className="addon-name">
                          GoPro rental
                          <span className="optional-badge">Optional</span>
                        </span>
                        <span className="addon-price">${GOPRO_PRICE} each</span>
                      </div>
                      <label className="qty-label" htmlFor="goproQty">Qty</label>
                      <select
                        id="goproQty"
                        name="goproQty"
                        value={formData.goproQty}
                        onChange={handleInputChange}
                      >
                        {Array.from({ length: quote.bikes + 1 }, (_, i) => i).map((count) => (
                          <option key={count} value={count}>{count}</option>
                        ))}
                      </select>
                    </div>
                    <div className="addon-option addon-qty">
                      <div className="addon-content">
                        <span className="addon-name">
                          Hat and t-shirt
                          <span className="optional-badge">Optional</span>
                        </span>
                        <span className="addon-price">${MERCH_PRICE} each</span>
                      </div>
                      <label className="qty-label" htmlFor="merchQty">Qty</label>
                      <select
                        id="merchQty"
                        name="merchQty"
                        value={formData.merchQty}
                        onChange={handleInputChange}
                      >
                        {Array.from({ length: quote.bikes + quote.pax + 1 }, (_, i) => i).map((count) => (
                          <option key={count} value={count}>{count}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Riding Information</h3>
                <div className="form-group">
                  <label htmlFor="experience">Riding Experience * <span className="optional-label">(lowest in the group)</span></label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select your experience level</option>
                    <option value="beginner">Beginner (0-2 years)</option>
                    <option value="intermediate">Intermediate (2-5 years)</option>
                    <option value="advanced">Advanced (5+ years)</option>
                    <option value="expert">Expert (10+ years)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="motorcycle">Motorcycle Make/Model *</label>
                  <textarea
                    id="motorcycle"
                    name="motorcycle"
                    value={formData.motorcycle}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="List each bike, e.g. Harley-Davidson Road King, Honda Gold Wing"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Emergency Contact</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="emergencyContact">Emergency Contact Name *</label>
                    <input
                      type="text"
                      id="emergencyContact"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emergencyPhone">Emergency Contact Phone *</label>
                    <input
                      type="tel"
                      id="emergencyPhone"
                      name="emergencyPhone"
                      value={formData.emergencyPhone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Additional Information</h3>
                <div className="form-group">
                  <label htmlFor="specialRequests">Special Requests or Dietary Restrictions</label>
                  <textarea
                    id="specialRequests"
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Any special dietary requirements, accessibility needs, or other requests..."
                  />
                </div>

                <p className="waiver-note">
                  Each rider and passenger will sign the{' '}
                  <a href="/Accident_Waiver_and_Release.pdf" className="terms-link" target="_blank" rel="noopener noreferrer">waiver</a>{' '}
                  before the tour. We'll send it once the booking is confirmed.
                </p>
              </div>

              <div className="form-submit">
                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                  onClick={() => trackButtonClick('book_now', 'booking_form')}
                >
                  {isSubmitting ? 'Processing...' : `Request Booking — $${formatPrice(quote.total)}`}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="success-message">
                    <span className="success-icon">✓</span>
                    <div>
                      <h4>Booking Submitted Successfully!</h4>
                      <p>We'll contact you within 24 hours to confirm your reservation and provide payment details.</p>
                    </div>
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <div>
                      <h4>Booking Failed</h4>
                      <p>There was an error submitting your booking. Please email us directly at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</p>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="booking-summary">
            <div className="summary-card">
              <h3>Booking Summary</h3>
              
              <div className="summary-item">
                <span>Package:</span>
                <span>{packageName(formData.package)}</span>
              </div>

              {quote.lines.map((line) => (
                <div key={line.key} className="summary-item">
                  <span>{line.label}</span>
                  <span>${formatPrice(line.amount)}</span>
                </div>
              ))}
              
              <div className="summary-total">
                <span>Group total:</span>
                <span>${formatPrice(quote.total)}</span>
              </div>
              
              <div className="summary-note">
                <p>* 50% deposit required to secure your spot</p>
                <p>* Balance due 30 days before departure</p>
              </div>
            </div>

            <div className="contact-info">
              <h4>Questions?</h4>
              <p>Contact us for more information:</p>
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <a href={`tel:${CONTACT_PHONE_TEL}`}>{CONTACT_PHONE}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
